import type { InputFrame } from '@nanocars/shared';

export type InputSource = 'keyboard' | 'gamepad';
export type MenuAction = 'up' | 'down' | 'left' | 'right' | 'confirm' | 'back';

const DEADZONE = 0.15;
const MENU_REPEAT_MS = 250;

// Gamepad button/axis indices per the standard mapping
// (https://www.w3.org/TR/gamepad/#remapping).
const BTN_A = 0; // throttle / confirm
const BTN_B = 1; // brake / back
const BTN_LT = 6; // brake
const BTN_RT = 7; // throttle
const BTN_DPAD_UP = 12;
const BTN_DPAD_DOWN = 13;
const BTN_DPAD_LEFT = 14;
const BTN_DPAD_RIGHT = 15;

interface KeyState {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  brake: boolean;
}

interface GamepadSample {
  throttle: number;
  brake: number;
  steer: number;
  dpadUp: boolean;
  dpadDown: boolean;
  confirm: boolean;
  back: boolean;
  active: boolean;
}

function isEditableTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
}

const MENU_KEY_MAP: Readonly<Record<string, MenuAction>> = {
  ArrowUp: 'up',
  w: 'up',
  W: 'up',
  ArrowDown: 'down',
  s: 'down',
  S: 'down',
  ArrowLeft: 'left',
  a: 'left',
  A: 'left',
  ArrowRight: 'right',
  d: 'right',
  D: 'right',
  Enter: 'confirm',
  ' ': 'confirm',
  Escape: 'back',
  Backspace: 'back',
};

// Keyboard + gamepad input, unified behind poll() for gameplay and a `menu`
// EventTarget for menu navigation. Construct once per app; call dispose()
// when the game shuts down (or between tests) to remove listeners and stop
// the gamepad-polling animation frame loop.
export class Input {
  private _source: InputSource = 'keyboard';
  private readonly keys: KeyState = { up: false, down: false, left: false, right: false, brake: false };
  private readonly menuTarget = new EventTarget();
  private readonly menuLastFiredAt: Partial<Record<MenuAction, number>> = {};
  private rafId: number | null = null;
  private disposed = false;

  private readonly onKeyDown = (e: KeyboardEvent): void => this.handleKey(e, true);
  private readonly onKeyUp = (e: KeyboardEvent): void => this.handleKey(e, false);

  constructor() {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    if (typeof requestAnimationFrame === 'function') {
      const loop = (now: number): void => {
        if (this.disposed) return;
        this.pollGamepadMenu(now);
        this.rafId = requestAnimationFrame(loop);
      };
      this.rafId = requestAnimationFrame(loop);
    }
  }

  get source(): InputSource {
    return this._source;
  }

  // Scenes navigate menus by listening for 'menu' CustomEvent<MenuAction>.
  get menu(): EventTarget {
    return this.menuTarget;
  }

  poll(seq: number): InputFrame {
    const gp = this.sampleGamepad();
    if (gp && gp.active) {
      this._source = 'gamepad';
      return { seq, throttle: gp.throttle, brake: gp.brake, steer: gp.steer };
    }
    const throttle = this.keys.up ? 1 : 0;
    const brake = this.keys.down || this.keys.brake ? 1 : 0;
    let steer = 0;
    if (this.keys.left) steer -= 1;
    if (this.keys.right) steer += 1;
    return { seq, throttle, brake, steer };
  }

  dispose(): void {
    this.disposed = true;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    if (this.rafId !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private handleKey(e: KeyboardEvent, down: boolean): void {
    // Menu text fields (name, room code) are backed by a real, focused DOM
    // <input> so typing works normally - arrow keys, WASD letters and
    // Enter/Backspace must reach the field as text input, not be swallowed
    // as driving/menu input by this window-level listener.
    if (isEditableTarget(e.target)) return;

    switch (e.key) {
      case 'ArrowUp':
      case 'w':
      case 'W':
        this.keys.up = down;
        break;
      case 'ArrowDown':
      case 's':
      case 'S':
        this.keys.down = down;
        break;
      case 'ArrowLeft':
      case 'a':
      case 'A':
        this.keys.left = down;
        break;
      case 'ArrowRight':
      case 'd':
      case 'D':
        this.keys.right = down;
        break;
      case ' ':
      case 'Spacebar':
      case 'Shift':
        this.keys.brake = down;
        break;
      default:
        break;
    }
    if (down) {
      this._source = 'keyboard';
      const action = MENU_KEY_MAP[e.key];
      if (action) this.dispatchMenu(action);
    }
  }

  private dispatchMenu(action: MenuAction): void {
    this.menuTarget.dispatchEvent(new CustomEvent<MenuAction>('menu', { detail: action }));
  }

  private sampleGamepad(): GamepadSample | null {
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return null;
    const pads = navigator.getGamepads();
    for (const gp of pads) {
      if (!gp) continue;
      return this.readPad(gp);
    }
    return null;
  }

  private readPad(gp: Gamepad): GamepadSample {
    const axisX = gp.axes[0] ?? 0;
    const dpadLeft = gp.buttons[BTN_DPAD_LEFT]?.pressed ?? false;
    const dpadRight = gp.buttons[BTN_DPAD_RIGHT]?.pressed ?? false;
    const dpadUp = gp.buttons[BTN_DPAD_UP]?.pressed ?? false;
    const dpadDown = gp.buttons[BTN_DPAD_DOWN]?.pressed ?? false;

    let steer = Math.abs(axisX) > DEADZONE ? axisX : 0;
    if (dpadLeft) steer = -1;
    if (dpadRight) steer = 1;

    const throttleBtn = (gp.buttons[BTN_RT]?.pressed ?? false) || (gp.buttons[BTN_A]?.pressed ?? false);
    const brakeBtn = (gp.buttons[BTN_LT]?.pressed ?? false) || (gp.buttons[BTN_B]?.pressed ?? false);
    const throttle = throttleBtn ? 1 : 0;
    const brake = brakeBtn ? 1 : 0;

    return {
      throttle,
      brake,
      steer,
      dpadUp,
      dpadDown,
      confirm: gp.buttons[BTN_A]?.pressed ?? false,
      back: gp.buttons[BTN_B]?.pressed ?? false,
      active: steer !== 0 || throttle > 0 || brake > 0 || dpadUp || dpadDown,
    };
  }

  private pollGamepadMenu(now: number): void {
    const gp = this.sampleGamepad();
    if (!gp) return;
    if (gp.active) this._source = 'gamepad';

    const candidates: ReadonlyArray<readonly [boolean, MenuAction]> = [
      [gp.steer < -DEADZONE, 'left'],
      [gp.steer > DEADZONE, 'right'],
      [gp.dpadUp, 'up'],
      [gp.dpadDown, 'down'],
      [gp.confirm, 'confirm'],
      [gp.back, 'back'],
    ];
    for (const [pressed, action] of candidates) {
      if (!pressed) {
        delete this.menuLastFiredAt[action];
        continue;
      }
      const last = this.menuLastFiredAt[action];
      if (last === undefined || now - last >= MENU_REPEAT_MS) {
        this.menuLastFiredAt[action] = now;
        this.dispatchMenu(action);
      }
    }
  }
}
